import { useEffect, useState } from "react";
import { Download, Loader2, Share2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { formatCurrency } from "@/lib/currency";
import { createGiftCardImage } from "@/lib/gift-card-image";
import type { GiftCardBrand, GiftCardVoucher } from "@/lib/gift-card-pdf";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

async function loadLogo(url: string): Promise<string> {
  const response = await fetch(url, { credentials: "include" });
  if (!response.ok) throw new Error("Store logo could not be loaded");
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => typeof reader.result === "string" ? resolve(reader.result) : reject(new Error("Invalid logo"));
    reader.onerror = () => reject(new Error("Store logo could not be read"));
    response.blob().then(blob => reader.readAsDataURL(blob), reject);
  });
}

export function ShareGiftCardDialog({ voucher, store, onClose }: { voucher: GiftCardVoucher; store: GiftCardBrand; onClose: () => void }) {
  const { toast } = useToast();
  const [card, setCard] = useState<{ file: File; url: string; logoIncluded: boolean } | null>(null);
  const [error, setError] = useState("");
  const [sharing, setSharing] = useState(false);
  useEffect(() => {
    let cancelled = false;
    let url: string | undefined;
    const logoUrl = store?.logoUrl ? `${BASE}/api/storage${store.logoUrl}` : null;
    createGiftCardImage(voucher, store, logoUrl ? () => loadLogo(logoUrl) : undefined)
      .then(result => {
        if (cancelled) return;
        url = URL.createObjectURL(result.file);
        setCard({ ...result, url });
      })
      .catch(err => { if (!cancelled) setError(err instanceof Error ? err.message : "Could not create the gift card."); });
    return () => { cancelled = true; if (url) URL.revokeObjectURL(url); };
  }, [voucher, store]);

  const canShareFile = !!card && typeof navigator.share === "function" && (() => {
    try {
      return typeof navigator.canShare === "function" && navigator.canShare({ files: [card.file] });
    } catch {
      return false;
    }
  })();
  const share = async () => {
    if (!card) return;
    setSharing(true);
    try {
      if (canShareFile) {
        // Prepare the file before this click to preserve browser user activation.
        await navigator.share({ files: [card.file], title: "Gift voucher" });
      } else {
        const message = [
          `Gift voucher from ${store?.name?.trim() || "our store"}`,
          `Voucher number: ${voucher.code}`,
          `Value: ${formatCurrency(voucher.value)}`,
          "Please present this voucher at checkout.",
        ].join("\n");
        const whatsappUrl = new URL("https://wa.me/");
        whatsappUrl.searchParams.set("text", message);
        window.open(whatsappUrl.toString(), "_blank", "noopener,noreferrer");
        toast({
          title: "WhatsApp opened",
          description: "Attach the saved PNG if you want to send the gift card image too.",
        });
      }
    } catch (err) {
      if (!(err instanceof Error && err.name === "AbortError")) {
        toast({ title: "Could not open sharing", description: "Save the PNG and attach it in WhatsApp.", variant: "destructive" });
      }
    } finally {
      setSharing(false);
    }
  };
  return (
    <Dialog open onOpenChange={open => { if (!open) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Share virtual gift card</DialogTitle>
          <DialogDescription>Send this card as an image on WhatsApp. Anyone with this voucher number may be able to redeem it. Share only with the intended recipient.</DialogDescription>
        </DialogHeader>
        {error ? <p role="alert" className="text-sm text-destructive">{error}</p> : !card ? (
          <div role="status" className="flex items-center justify-center gap-2 py-12"><Loader2 className="h-5 w-5 animate-spin" /> Preparing card…</div>
        ) : (
          <>
            <img src={card.url} alt={`Gift voucher ${voucher.code}, value ${formatCurrency(voucher.value)}`} className="w-full rounded-lg" />
            {store?.logoUrl && !card.logoIncluded && <p className="text-sm text-amber-700">The store logo could not be loaded. This card is ready to share without it.</p>}
            <p className="text-sm text-muted-foreground">{canShareFile ? "Tap Send to WhatsApp, then choose WhatsApp and your recipient." : "WhatsApp will open with the voucher details. To include the card image, save the PNG and attach it in the chat."}</p>
            <div className="flex flex-wrap gap-2">
              <Button onClick={share} disabled={sharing} className="gap-2"><Share2 className="h-4 w-4" />{sharing ? "Opening WhatsApp…" : "Send to WhatsApp"}</Button>
              <Button asChild variant="outline"><a href={card.url} download={card.file.name}><Download className="mr-2 h-4 w-4" /> Save PNG</a></Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
